import { PlatformType } from '../context/socialProvider';
import { CategoryScores } from '../types/categoryScores';
import { API } from './index';
import Cookies from 'js-cookie';

export const createSocial = async (accessToken: String | null) => {
  try {
    const response = await API.post('/api/v1/social/create', {
      accessToken,
    });

    return response;
  } catch (error: any) {
    throw error.response.data;
  }
};

// Update the setMain function to support multiple social IDs
export const setMain = async (
  userId: number,
  instagramSocialId?: number,
  facebookSocialId?: number,
  metaAdsSocialId?: number
) => {
  try {
    const payload: any = { userId };

    if (instagramSocialId) payload.instagramSocialId = instagramSocialId;
    if (facebookSocialId) payload.facebookSocialId = facebookSocialId;
    if (metaAdsSocialId) payload.metaAdsSocialId = metaAdsSocialId;

    const response = await API.post('/api/v1/social/set_main', payload);
    return response.data;
  } catch (error: any) {
    throw error.response.data;
  }
};

export const getSocialMains = async () => {
  try {
    const response = await API.get('/api/v1/socials/get-mains');
    return response.data.result;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw error.response.data;
    } else {
      throw error;
    }
  }
};

export const updateToken = async (socialId: number, accessToken: string) => {
  console.log(socialId, accessToken);
  try {
    const response = await API.put('/api/v1/social/access_token', {
      id: socialId,
      accessToken: accessToken,
    });

    return response.data;
  } catch (error: any) {
    throw error.response.data;
  }
};

export const updateBio = async (id: number, bio: string) => {
  try {
    const response = await API.put(`/api/v1/social/update-bio`, { id, bio });
  } catch (error: any) {
    throw error.response.data;
  }
};

export const updateDmInstructions = async (
  id: number,
  dmInstructions: string
) => {
  const authToken = Cookies.get('accessToken');

  try {
    const response = await API.put(
      `/api/v1/social/update-dm-instructions`,
      { id, dmInstructions },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
  } catch (error: any) {
    throw error.response.data;
  }
};

export const updateProfileDescription = async (
  id: number,
  profileDescription: string
) => {
  const authToken = Cookies.get('accessToken');

  try {
    const response = await API.put(
      `/api/v1/social/update-profile-analysis`,
      { id, profileAnalysis: profileDescription },
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      }
    );
  } catch (error: any) {
    throw error.response.data;
  }
};

export const updateAutomod = async (
  socialId: number,
  autoModActive?: boolean,
  autoModInstructions?: string,
  categoryScores?: CategoryScores
): Promise<any> => {
  try {
    const requestBody: any = { socialId };

    if (autoModActive !== undefined) {
      requestBody.autoModActive = autoModActive;
    }
    if (autoModInstructions !== undefined) {
      requestBody.autoModInstructions = autoModInstructions;
    }
    if (categoryScores !== undefined) {
      requestBody.categoryScores = categoryScores;
    }

    const response = await API.put(
      '/api/v1/social/update-automod',
      requestBody
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

export const updateSocial = async (
  socialId: number,
  column: string,
  value: any
): Promise<any> => {
  const authToken = Cookies.get('accessToken');

  const response = await API.put(
    `/api/v1/social/update`,
    {
      socialId,
      column,
      value,
    },
    {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  );
  return response;
};

export const createCommonResponse = async (
  socialId: number,
  commentExample: string,
  suggestedOutput: string
) => {
  const authToken = Cookies.get('accessToken');
  const response = await API.post(
    '/api/v1/common-responses/create',
    {
      socialId,
      type: 'comment',
      commentExample,
      suggestedOutput,
    },
    {
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  return response.data;
};

export const updateCommonResponse = async (
  id: number,
  socialId: number,
  commentExample: string,
  suggestedOutput: string
) => {
  const authToken = Cookies.get('accessToken');
  const response = await API.put(
    '/api/v1/common-responses/update',
    {
      id,
      socialId,
      type: 'comment',
      commentExample,
      suggestedOutput,
    },
    {
      headers: { Authorization: `Bearer ${authToken}` },
    }
  );
  return response.data;
};

export const deleteCommonResponse = async (id: number, socialId: number) => {
  const authToken = Cookies.get('accessToken');
  const response = await API.delete('/api/v1/common-responses/delete', {
    params: { id, socialId },
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return response.data;
};

/**
 * Create a new comment moderation example
 * @param request - The example data to create
 * @returns Promise<CommentModerationExample>
 */
export const createCommentModerationExample = async (
  request: CommentModerationExampleRequest
): Promise<CommentModerationExample> => {
  try {
    const response = await API.post(
      '/api/v1/social/moderation-examples/create',
      request
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

/**
 * Comment Moderation Example interfaces
 */
export interface CommentModerationExample {
  id: number;
  socialId: number;
  commentExample: string;
  reasoning: string;
  rule: string;
}

export interface CommentModerationExampleRequest {
  socialId: number;
  commentExample: string;
  reasoning: string;
  rule: string;
}

/**
 * Update an existing comment moderation example
 * @param id - The ID of the example to update
 * @param request - The updated example data
 * @returns Promise<CommentModerationExample>
 */
export const updateCommentModerationExample = async (
  id: number,
  request: CommentModerationExampleRequest
): Promise<CommentModerationExample> => {
  try {
    const response = await API.put(
      `/api/v1/social/moderation-examples/${id}`,
      request
    );
    return response.data;
  } catch (error: any) {
    throw error.response?.data || error;
  }
};

/**
 * Delete a comment moderation example
 * @param id - The ID of the example to delete
 * @param socialId - The social ID for authorization
 * @returns Promise<void>
 */
export const deleteCommentModerationExample = async (
  id: number,
  socialId: number
): Promise<void> => {
  try {
    await API.delete(`/api/v1/social/moderation-examples/${id}`, {
      params: { socialId },
    });
  } catch (error: any) {
    throw error.response?.data || error;
  }
};
